import { cn } from '@virginexperiencedays/tailwind';
import { useServerToClientDate } from '@virginexperiencedays/hooks';

import {
  PDPReviewCard,
  PDPReviewCardContent,
  PDPReviewCardTitle,
  PDPReviewMetadata,
  PDPReviewResponse,
  PDPReviewStarRating,
} from '@virginexperiencedays/components-v2/src/cards/Review/PDPReviewCard';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@virginexperiencedays/components-v2/src/CollapsibleContent';
import {
  BodyText,
  type BodyTextProps,
} from '@virginexperiencedays/components-v2/src/typography/BodyText';
import { IconStarFull } from '@virginexperiencedays/components-v2/src/icons/IconStar';

import type { HTMLAttributes } from 'react';
import type { Review, Reviews } from '../VariationPage/extractors';

type ReviewCardProps = HTMLAttributes<HTMLDivElement> &
  Omit<Review, 'reply'> & {
    response: {
      content: Review['reply'];
      responderName: Review['responderName'];
    };
    isContentFlexGrow?: boolean;
    onExpandCollapse?: (props: { isExpanded: boolean }) => void;
  };

export const ReviewCard = ({
  rating,
  title,
  content,
  reviewerName,
  dateCreated,
  response,
  isContentFlexGrow,
  onExpandCollapse,
  ...rest
}: ReviewCardProps) => {
  const date = useServerToClientDate(dateCreated, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <PDPReviewCard {...rest}>
      <PDPReviewStarRating rating={rating} className="justify-start" />

      <PDPReviewCardTitle title={title} />

      <Collapsible
        className={cn('flex flex-col items-start gap-2', isContentFlexGrow && 'flex-1')}
        lineClampCount={3}
      >
        <CollapsibleContent>
          <PDPReviewCardContent content={content} />
        </CollapsibleContent>
        <CollapsibleTrigger
          className="font-semibold text-brand underline"
          onClick={({ isExpanded }) => {
            onExpandCollapse?.({ isExpanded });
          }}
        />
      </Collapsible>

      <PDPReviewMetadata reviewerName={reviewerName} dateCreated={date} />

      {response && <PDPReviewResponse className="mt-4" {...response} />}
    </PDPReviewCard>
  );
};

type ReviewHeadingProps = BodyTextProps & {
  count: number;
  averageRating: string;
  size?: 'large' | 'small';
};

export const ReviewHeading = ({
  className,
  averageRating,
  count,
  size = 'large',
  ...rest
}: ReviewHeadingProps) => (
  <BodyText className={cn('flex items-baseline gap-2 leading-none', className)} as="div" {...rest}>
    <IconStarFull
      className={cn('h-5 w-5', size === 'small' && 'h-4 w-4')}
      shadedcolor="var(--text-neutral-strong)"
    />

    <span className={cn('text-4xl font-medium leading-none', size === 'small' && 'text-[22px]')}>
      {averageRating}
    </span>

    <span
      className={cn(
        'text-base leading-none text-neutral-faded',
        size === 'small' && 'text-sm leading-none'
      )}
    >
      {count} Review{count > 1 && 's'}
    </span>
  </BodyText>
);

export const ReviewSummary = ({
  averageRating,
  averageRatingDisplay,
  className,
}: Pick<Reviews, 'averageRating' | 'averageRatingDisplay'> & HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center gap-1 text-sm font-medium leading-normal text-neutral-faded',
      className
    )}
    data-rating={averageRating}
  >
    <IconStarFull className="h-4 w-4" shadedcolor="var(--text-neutral-strong)" />

    <span data-testid="average-rating-display">{averageRatingDisplay}</span>
  </div>
);

export const ReviewCardSkeleton = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1.5', className)} {...rest}>
    <PDPReviewStarRating rating={0} className="justify-start" />
    <div className="h-5 w-52 max-w-full animate-pulse bg-background-neutral-faded" />
    <div className="h-5 w-full max-w-full animate-pulse bg-background-neutral-faded" />
    <div className="h-5 w-full max-w-full animate-pulse bg-background-neutral-faded" />
    <div className="h-5 w-24 max-w-full animate-pulse bg-background-neutral-faded" />
    <div className="h-4 w-72 max-w-full animate-pulse bg-background-neutral-faded" />
  </div>
);

export const ReviewHeadingSkeleton = ({ className, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-baseline gap-2', className)} {...rest}>
    <IconStarFull className="h-5 w-5" shadedcolor="var(--text-neutral-strong)" />

    <div className="h-6 w-8 animate-pulse rounded-sm bg-background-neutral-faded" />

    <div className="h-3.5 w-20 animate-pulse rounded-sm bg-background-neutral-faded" />
  </div>
);
